import {useId, useRef, useState} from 'react'
import {StopIcon} from '@primer/octicons-react'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {Banner, Button, Dialog, Flash} from '@primer/react'
import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
import type {PullRequestUserActionStack} from '@github-ui/pull-requests/analytics/pull-request-user-action'

import styles from './StackSection.module.css'
import {MergeBoxExpandable} from './common/MergeBoxExpandable'
import {MergeBoxSectionHeader} from './common/MergeBoxSectionHeader'
import {CenteredLoadingSpinner} from './common/CenteredLoadingSpinner'
import {RebaseStackConfirmationDialog} from './common/RebaseStackConfirmationDialog'
import type {
  StackSectionStatusType,
  StackSectionStackEntry,
} from '../../helpers/merge-box-status-calculator/stack-section-status'
import {
  RebaseState,
  useStackRebaseStatus,
  type StackRebasePullRequestEntry,
} from '../../hooks/mutations/use-stack-rebase-mutation'
import {useRebaseStack} from '../../hooks/use-rebase-stack'
import {StackStatusIcon} from './stack-section/StatusIcon'
import {useDisableAutoMergeMutation} from '../../hooks/mutations/use-disable-auto-merge-mutation'
import {clsx} from 'clsx'
import {StackList} from './stack-section/StackList'

export function StackSection({
  pullRequestId,
  status,
  loadingEntryCount,
  baseRefName,
  showRebaseButton = true,
  stackBaseRefRequiresSignatures = false,
  stackEntries,
  isStackMergeInProgress = false,
  viewerCanDisableAutoMerge = false,
  shouldOnlyRenderStackList = false,
  stackMetadata,
  onConfirmRebase,
}: {
  pullRequestId: string
  status: StackSectionStatusType
  loadingEntryCount: number
  baseRefName?: string
  showRebaseButton?: boolean
  stackBaseRefRequiresSignatures?: boolean
  stackEntries: StackSectionStackEntry[]
  isStackMergeInProgress?: boolean
  viewerCanDisableAutoMerge?: boolean
  // Hides the section header and rebase actions, leaving only the list of stacked pull requests in an always expanded container.
  shouldOnlyRenderStackList?: boolean
  stackMetadata?: PullRequestUserActionStack
  // When provided, confirming the rebase hands off to the parent (which renders
  // `RebasingStacksSection`) instead of starting the rebase mutation inline.
  onConfirmRebase?: () => void
}) {
  const stackSectionAriaId = useId()
  const [stackExpanded, setStackExpanded] = useSessionStorage<boolean>(
    `${pullRequestId}:stackExpanded`,
    status !== 'PASSED' || showRebaseButton,
  )
  const {isRebasing, status: rebaseState} = useStackRebaseStatus()
  const {onRebaseStack, rebaseErrorMessage} = useRebaseStack({stackEntries, stackMetadata})
  const [cancelMergeError, setCancelMergeError] = useState<string | undefined>()

  const {mutate: disableAutoMerge, isPending: isCancelMergePending} = useDisableAutoMergeMutation({
    onError: (e: Error) => {
      setCancelMergeError(e.message)
    },
  })

  const handleCancelMerge = () => {
    if (isCancelMergePending) return
    setCancelMergeError(undefined)
    disableAutoMerge()
  }

  const otherEntriesToMerge = stackEntries.filter(e => e.position === 'BEFORE' && e.pull.state !== 'MERGED')

  let title
  let subtitle
  let anyPullsAreLoading = false
  const isFirstUnmergedPRInStack = otherEntriesToMerge.length === 0

  const entriesToMergeCount = stackEntries.filter(
    e => (e.position === 'BEFORE' || e.position === 'CURRENT') && e.pull.state !== 'MERGED',
  ).length

  switch (true) {
    case isStackMergeInProgress && entriesToMergeCount === 1:
      title = 'Merging the first pull request in the stack…'
      subtitle = 'This pull request is being merged.'
      break
    case isStackMergeInProgress:
      title = 'Merging stack…'
      subtitle = `${entriesToMergeCount} pull requests are being merged.`
      break
    case isRebasing && Object.keys(rebaseState).length > 0: {
      const states = Object.values(rebaseState).map(e => e.state)
      const pendingCount = states.filter(s => s === RebaseState.Rebasing).length
      const succeededCount = states.filter(s => s === RebaseState.Rebased).length
      const failedCount = states.filter(
        s => s === RebaseState.FailedToRebase || s === RebaseState.MergeConflicts,
      ).length
      title = 'Rebasing stack…'
      const parts: string[] = []
      if (pendingCount > 0) parts.push(`${pendingCount} in progress`)
      if (succeededCount > 0) parts.push(`${succeededCount} completed`)
      if (failedCount > 0) parts.push(`${failedCount} failed`)
      subtitle = parts.join(', ')
      break
    }
    case isRebasing:
      title = 'Rebasing stack…'
      subtitle = 'Starting rebase.'
      break
    case (status === 'FAILED' || status === 'BLOCKED') && isFirstUnmergedPRInStack:
      title = 'Unable to merge'
      subtitle = 'This pull request cannot be merged.'
      break
    case status === 'FAILED' || status === 'BLOCKED':
      title = 'Unable to merge as a stack'
      subtitle = 'Some of the pull requests in this stack cannot be merged.'
      break
    case status === 'PASSED' && isFirstUnmergedPRInStack:
      title = 'Able to merge the first pull request in the stack'
      subtitle = 'Only this pull request will be merged.'
      break
    case status === 'PASSED':
      title = 'Able to merge as a stack'
      subtitle = `Merging this pull request will also merge ${otherEntriesToMerge.length} pull request${otherEntriesToMerge.length === 1 ? '' : 's'} below it.`
      break
    case status === 'LOADING_MERGE_STATE':
      anyPullsAreLoading = true
      title = 'Loading stack status...'
      subtitle = `${loadingEntryCount} of ${stackEntries.length} pull requests loading`
      break
    case status === 'PENDING':
      title = 'Waiting on status checks...'
      subtitle = 'Some of the pull requests in this stack have pending status checks.'
      break
    default:
      title = 'Could not determine stack status'
      subtitle = 'Refresh the page to try again.'
  }

  return (
    <section
      aria-label="Stacked pull requests"
      aria-describedby={shouldOnlyRenderStackList ? undefined : stackSectionAriaId}
      className={`${styles.StackSection} border-bottom color-border-subtle`}
    >
      {!shouldOnlyRenderStackList && (
        <MergeBoxSectionHeader
          headerId={stackSectionAriaId}
          title={title}
          subtitle={subtitle}
          icon={
            isRebasing || anyPullsAreLoading || isStackMergeInProgress ? (
              <CenteredLoadingSpinner className={styles.StackSectionLoadingSpinner} />
            ) : (
              <StackStatusIcon status={status} isFirstUnmergedPRInStack={isFirstUnmergedPRInStack} />
            )
          }
          {...(isStackMergeInProgress
            ? {
                rightSideContent: (
                  <Button
                    disabled={!viewerCanDisableAutoMerge}
                    onClick={handleCancelMerge}
                    loading={isCancelMergePending}
                    loadingAnnouncement="Cancelling stack merge"
                  >
                    Cancel merge
                  </Button>
                ),
              }
            : {
                expandableProps: {
                  ariaLabel: stackExpanded ? 'Collapse stack details' : 'Expand stack details',
                  isExpanded: stackExpanded,
                  onToggle: () => {
                    setStackExpanded(!stackExpanded)
                  },
                },
              })}
        />
      )}
      <MergeBoxExpandable isExpanded={shouldOnlyRenderStackList || isStackMergeInProgress || stackExpanded}>
        {showRebaseButton && !isRebasing && (
          <RebaseButtonBanner
            onRebase={onConfirmRebase ?? onRebaseStack}
            outOfDateCount={stackEntries.filter(e => e.pull.state !== 'MERGED').length}
            stackBaseRefRequiresSignatures={stackBaseRefRequiresSignatures}
          />
        )}
        {Object.keys(rebaseState).length > 0 && !isRebasing && <RebaseSummaryBanner rebaseState={rebaseState} />}
        <StackList
          baseRefName={baseRefName}
          currentPullRequestId={pullRequestId}
          stackMetadata={stackMetadata}
          stackEntries={stackEntries}
        />
      </MergeBoxExpandable>
      {rebaseErrorMessage && (
        <Flash className="tmp-m-3" variant="danger" role="alert">
          <StopIcon className="mr-2" />
          {rebaseErrorMessage}
        </Flash>
      )}
      {cancelMergeError && (
        <Flash className="tmp-m-3" variant="danger" role="alert">
          <StopIcon className="mr-2" />
          {cancelMergeError}
        </Flash>
      )}
    </section>
  )
}

function RebaseButtonBanner({
  onRebase,
  outOfDateCount,
  stackBaseRefRequiresSignatures,
}: {
  onRebase: () => void
  outOfDateCount: number
  stackBaseRefRequiresSignatures: boolean
}) {
  const [showDialog, setShowDialog] = useState(false)
  const primaryActionRef = useRef<HTMLButtonElement>(null)
  return (
    <>
      <Banner
        className="mx-2 mt-2"
        variant="warning"
        hideTitle
        title="This stack needs to be rebased"
        description="Some pull requests in this stack are out of date with previous changes and need to be rebased."
        primaryAction={
          <Banner.PrimaryAction ref={primaryActionRef} onClick={() => setShowDialog(true)}>
            Rebase stack
          </Banner.PrimaryAction>
        }
      />
      {showDialog && stackBaseRefRequiresSignatures && (
        <Dialog
          className={styles.RebaseConfirmationDialog}
          title="Rebase stack via the command line"
          returnFocusRef={primaryActionRef}
          onClose={() => {
            setShowDialog(false)
            primaryActionRef.current?.focus()
          }}
        >
          <p className="mb-2">
            Stacked pull requests rely on rebasing rather than merge commits to update your branch. Instead of merging
            upstream changes into your branch, a rebase will replay the commits from your head branch on top of the
            latest base.
          </p>
          {/* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- baselined during linter rollout */}
          <h4 className="mb-2">CLI Instructions</h4>
          <p className="mb-1">
            Use the <a href="https://gh.io/stacks">gh stack</a> CLI to rebase your stack of branches and push your
            changes to remote.
          </p>
          <p className="mb-2 mt-3">
            <strong>Step 1</strong> Switch to the head branch of the pull request in the stack.
          </p>
          <div className={styles.codeBlock}>
            <pre>
              <code>gh stack checkout [branch]</code>
            </pre>
            <CopyToClipboardButton
              textToCopy="gh stack checkout [branch]"
              ariaLabel="Copy checkout command to clipboard"
              size="small"
            />
          </div>
          <p className="mb-2 mt-3">
            <strong>Step 2</strong> Rebase the stack with changes from the base branch.
          </p>
          <div className={styles.codeBlock}>
            <pre>
              <code>gh stack rebase</code>
            </pre>
            <CopyToClipboardButton
              textToCopy="gh stack rebase"
              ariaLabel="Copy rebase command to clipboard"
              size="small"
            />
          </div>
          <p className="mb-2 mt-3">
            <strong>Step 3</strong> Fix any conflicts and commit the result.
          </p>
          <p className="mt-2">
            See <a href="https://gh.io/stacks-resolving-conflicts">Resolving a merge conflict</a> for step-by-step
            instructions on resolving merge conflicts.
          </p>
          <p className="mb-2 mt-3">
            <strong>Step 4</strong> Push the changes.
          </p>
          <div className={styles.codeBlock}>
            <pre>
              <code>gh stack push</code>
            </pre>
            <CopyToClipboardButton textToCopy="gh stack push" ariaLabel="Copy push command to clipboard" size="small" />
          </div>
        </Dialog>
      )}
      {showDialog && !stackBaseRefRequiresSignatures && (
        <RebaseStackConfirmationDialog
          outOfDateCount={outOfDateCount}
          onConfirm={onRebase}
          onClose={() => {
            setShowDialog(false)
            primaryActionRef.current?.focus()
          }}
        />
      )}
    </>
  )
}

function RebaseSummaryBanner({rebaseState}: {rebaseState: Record<number, StackRebasePullRequestEntry>}) {
  const entries = Object.values(rebaseState)
  const succeededCount = entries.filter(e => e.state === RebaseState.Rebased).length
  const mergeConflictEntries = entries.filter(e => e.state === RebaseState.MergeConflicts)
  const failedEntries = entries.filter(e => e.state === RebaseState.FailedToRebase)
  const failedCount = failedEntries.concat(mergeConflictEntries).length

  const [hideSuccessBanner, setHideSuccessBanner] = useState(false)
  const [hideFailureBanner, setHideFailureBanner] = useState(false)
  if (entries.length === 0) return null

  const allSucceeded = failedCount === 0

  let succeededDescription = ''
  let failedDescription = ''
  if (allSucceeded) {
    succeededDescription = `Successfully rebased ${succeededCount} pull request${succeededCount === 1 ? '' : 's'}.`
  } else {
    if (entries.length > 1) {
      failedDescription = `${failedCount} of ${entries.length} pull requests failed to rebase.`
    } else {
      failedDescription = `1 pull request failed to rebase.`
    }

    for (const failedEntry of failedEntries) {
      if (failedEntry.message) {
        failedDescription += `\n- ${failedEntry.message} for #${failedEntry.number}`
      } else {
        // Fallback to generic message if message is `null` which should be unlikely
        // as messages are always defined on failure in
        // github/github/packages/pull_requests/app/models/pull_requests/stacks/update_all_branches/service.rb
        failedDescription += `\n- Failed to rebase for #${failedEntry.number}`
      }
    }

    for (const mergeConflictEntry of mergeConflictEntries) {
      failedDescription += `\n- Merge conflicts with #${mergeConflictEntry.number}`
    }
  }

  return (
    <>
      {allSucceeded && !hideSuccessBanner && (
        <Banner
          className="mx-2 mt-2"
          aria-label="Rebase success summary"
          variant={'success'}
          title="Rebase complete"
          hideTitle
          description={succeededDescription}
          onDismiss={() => setHideSuccessBanner(true)}
        />
      )}
      {failedCount > 0 && !hideFailureBanner && (
        <Banner
          className={clsx(styles.preLine, 'mx-2 mt-2')}
          aria-label="Rebase failure summary"
          variant={'critical'}
          title="Rebase failed"
          hideTitle
          description={failedDescription}
          onDismiss={() => setHideFailureBanner(true)}
        />
      )}
    </>
  )
}
